import ceil from 'lodash/ceil';
import round from 'lodash/round';

import type {
  ExtendedPriceInfo,
  PriceInfo,
} from '@shared/types/frontend/hotel-card';
import type { PriceBreakdownInfo } from '@shared/types/frontend/price-breakdown';
import type { OfferPrice } from '@shared/types/frontend/ubff/carousel-hotels';
import type { RatePrice } from '@shared/types/frontend/ubff/hotel-detail-price';
import type { I18nService } from '@shared/types/i18n';
import type { TaxAndFeeInclusionPolicy } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/common.pb';

import {
  PRICE_TYPE_PER_NIGHT as PRICE_TYPE_PER_NIGHT_UBFF,
  TAX_AND_FEE_INCLUSION_POLICY_FEES_INCLUDED,
  TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_INCLUDED,
  TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_NOT_INCLUDED,
} from '@shared/constants/ubff-gateway';
import {
  isFeesIncluded,
  isTaxesFeesIncluded,
} from '@shared/utils-universal/price-policy';
import { PRICE_TYPE_PER_NIGHT } from '@shared/utils-universal/price-type';

type DVPriceBreakdownInfoProps = {
  priceInfo: PriceInfo & ExtendedPriceInfo;
  i18n: I18nService;
  numberOfNights: number;
  pricePolicy: string;
  priceType: string;
};

type HDPriceBreakdownInfoProps = {
  priceInfo: RatePrice;
  numberOfNights: number;
  priceType?: string;
  taxAndFeeInclusionPolicy?: TaxAndFeeInclusionPolicy;
};

type CarouselHotelsPriceBreakdownInfoProps = {
  offerPrice: OfferPrice;
  numberOfNights: number;
  priceType?: string;
  taxAndFeeInclusionPolicy?: TaxAndFeeInclusionPolicy;
};

export const buildPriceBreakdownInfoForDV = ({
  i18n: { translate },
  numberOfNights,
  priceInfo: {
    basePrice,
    cug,
    originalBasicPricePerNight,
    originalBasicPriceTotal,
    price,
    priceWithAllTaxes,
    priceWithAllTaxesPerNight,
    secondaryPrice,
    taxesAndFees,
  },
  pricePolicy,
  priceType,
}: DVPriceBreakdownInfoProps): PriceBreakdownInfo => {
  const isPerNightType = priceType === PRICE_TYPE_PER_NIGHT;

  let taxAndFeeInclusionPolicy: TaxAndFeeInclusionPolicy;
  if (isTaxesFeesIncluded(pricePolicy)) {
    taxAndFeeInclusionPolicy = TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_INCLUDED;
  } else if (isFeesIncluded(pricePolicy)) {
    taxAndFeeInclusionPolicy = TAX_AND_FEE_INCLUSION_POLICY_FEES_INCLUDED;
  } else {
    taxAndFeeInclusionPolicy =
      TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_NOT_INCLUDED;
  }

  return {
    priceInfo: {
      // price is calculated to integer in bff but required 2 decimal places in total cost
      price: isPerNightType ? ceil(secondaryPrice / numberOfNights, 2) : price,
      secondaryPrice,
      basicPrice: basePrice,
      priceWithAllTaxes,
      paymentSchedules: [],
      tags: [],
      originalBasicPricePerNight,
      originalBasicPriceTotal,
      priceWithAllTaxesPerNight,
      discountInfo: cug
        ? {
            priceWithoutDiscount: cug.originalPrice || 0,
            discountType: cug.type,
            discountAmount: cug.discountPrice || 0,
            percentage: cug.discountPercentage || 0,
          }
        : null,
      taxesAndFees: taxesAndFees
        ? {
            totalTaxes: round(priceWithAllTaxes - basePrice, 2),
            taxTypes: taxesAndFees.map(({ key, total }) => ({
              type: 'TAX_UNSPECIFIED',
              price: total,
              title: translate(key, { 0: total }),
              paymentType: 'PAYMENT_TYPE_UNSPECIFIED',
            })),
          }
        : null,
    },
    isPerNightType,
    numberOfNights,
    taxAndFeeInclusionPolicy,
  };
};

export const buildPriceBreakdownInfoForHD = ({
  numberOfNights,
  priceInfo,
  priceType = PRICE_TYPE_PER_NIGHT_UBFF,
  taxAndFeeInclusionPolicy = TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_NOT_INCLUDED,
}: HDPriceBreakdownInfoProps): PriceBreakdownInfo => {
  const isPerNightType = priceType === PRICE_TYPE_PER_NIGHT_UBFF;

  return {
    priceInfo,
    isPerNightType,
    numberOfNights,
    taxAndFeeInclusionPolicy,
  };
};

export const buildPriceBreakdownInfoForCarouselHotels = ({
  numberOfNights,
  offerPrice,
  priceType = PRICE_TYPE_PER_NIGHT_UBFF,
  taxAndFeeInclusionPolicy = TAX_AND_FEE_INCLUSION_POLICY_TAXES_FEES_NOT_INCLUDED,
}: CarouselHotelsPriceBreakdownInfoProps): PriceBreakdownInfo => {
  const isPerNightType = priceType === PRICE_TYPE_PER_NIGHT_UBFF;
  const { discountInfo, pricePerNight, priceTotal, taxesAndFees } = offerPrice;

  return {
    isPerNightType,
    numberOfNights,
    taxAndFeeInclusionPolicy,
    priceInfo: {
      price: isPerNightType
        ? pricePerNight.displayPrice
        : priceTotal.displayPrice,
      secondaryPrice: isPerNightType
        ? priceTotal.displayPrice
        : pricePerNight.displayPrice,
      basicPrice: priceTotal.basicPrice,
      priceWithAllTaxes: priceTotal.priceWithAllTaxes,
      paymentSchedules: [],
      tags: [],
      originalBasicPricePerNight: pricePerNight.basicPriceWithoutDiscount,
      originalBasicPriceTotal: priceTotal.basicPriceWithoutDiscount,
      priceWithAllTaxesPerNight: pricePerNight.priceWithAllTaxes,
      discountInfo,
      taxesAndFees: taxesAndFees
        ? {
            totalTaxes: taxesAndFees.totalTaxes,
            taxTypes: taxesAndFees.taxes.map(({ taxType, ...rest }) => ({
              type: taxType,
              ...rest,
            })),
          }
        : null,
    },
  };
};

export const enableSubPrice = (totalCostStyleVar: string) =>
  !!(totalCostStyleVar && totalCostStyleVar.toUpperCase() === 'WITH_SUB_PRICE');

export const enablePurePrice = (totalCostStyleVar: string) =>
  !!(
    totalCostStyleVar && totalCostStyleVar.toUpperCase() === 'WITH_PURE_PRICE'
  );

export const getSubPriceLabel = ({
  i18n: { formatCurrency, translate },
  isPerNightType,
  price,
}: {
  isPerNightType: boolean;
  price: number;
  i18n: I18nService;
}) => {
  const priceLabel = formatCurrency(price);

  return isPerNightType
    ? translate('price_breakdown_totalPrice', {
        0: priceLabel,
      })
    : translate('OTARate_label_priceForANight', {
        price: priceLabel,
      });
};

export const getPriceTypeLabel = ({
  i18n: { translate },
  isMobile,
  isPerNightType,
  shouldShowTotalWithTaxesPrice,
  taxesIncluded,
}: {
  isPerNightType: boolean;
  taxesIncluded: boolean;
  isMobile: boolean;
  i18n: I18nService;
  shouldShowTotalWithTaxesPrice: boolean;
}) => {
  if (isPerNightType) return translate('dayView_hotel_card_a_night');

  return taxesIncluded || isMobile || shouldShowTotalWithTaxesPrice
    ? translate('dayView_hotel_card_total')
    : translate('TotalCost_total_before_taxes');
};

export const getButtonCopyKey = (hasDeeplink: boolean) => {
  if (hasDeeplink) {
    return 'OTARates_button_goToSite';
  }

  return 'Modal_close';
};

export const getSwitchLabel = ({
  i18n: { translate },
  isMobile,
}: {
  i18n: I18nService;
  isMobile: boolean;
}) => {
  if (isMobile) return translate('TotalCost_total_including_taxes');

  return translate('TotalCost_display_total_including_taxes');
};
